/**
 * Allocation maths for payments, refunds and notes (ACCOUNTING_RULES §4) — pure, no React.
 *
 * Amounts travel as decimal strings (`"1250.00"`); to stay exact they are handled here as integer
 * ten-thousandths (`bigint`), the API's maximum precision (`\d{1,15}(\.\d{1,4})?`).
 */

const SCALE = 4;
const FACTOR = 10n ** BigInt(SCALE);

/** Parses a decimal string into ten-thousandths; null when it is not a plain non-negative decimal. */
export function toUnits(value: string | number | null | undefined): bigint | null {
  if (value === null || value === undefined) return null;
  const s = String(value).trim().replace(/,/g, '');
  if (s === '') return null;
  const m = /^(-)?(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return null;
  const frac = m[3] ?? '';
  if (frac.length > SCALE) return null;
  const units = BigInt(m[2] || '0') * FACTOR + BigInt((frac + '0000').slice(0, SCALE));
  return m[1] ? -units : units;
}

/** Ten-thousandths back to a decimal string with `decimals` fraction digits (no rounding needed). */
export function fromUnits(units: bigint, decimals = 2): string {
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = abs / FACTOR;
  const frac = (abs % FACTOR).toString().padStart(SCALE, '0');
  // Keep the significant digits even beyond `decimals`, so nothing is silently lost.
  const trimmed = frac.replace(/0+$/, '');
  const shown = trimmed.length > decimals ? trimmed : frac.slice(0, decimals);
  return `${neg ? '-' : ''}${whole.toString()}${shown ? `.${shown}` : ''}`;
}

const zero = 0n;
const min = (a: bigint, b: bigint) => (a < b ? a : b);

/** An open document a credit can settle (an entry of `claims` in /contacts/{id}/open-items). */
export interface OpenItem {
  type: string;
  id: string;
  number: string | null;
  date: string;
  open: string;
}

/**
 * The API's FIFO order: oldest business date first, then document number, then id. (The server also
 * uses the posting time, which open items do not expose; their response order already reflects it, so
 * the sort is stable for equal keys.)
 */
export function fifoOrder<T extends OpenItem>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (a.item.date !== b.item.date) return a.item.date < b.item.date ? -1 : 1;
      return a.index - b.index;
    })
    .map((x) => x.item);
}

/** Amount per document id, as decimal strings (the shape the forms edit). */
export type AllocationDraft = Record<string, string>;

/**
 * Oldest-first allocation of `amount`: each document gets min(remaining, its open amount); whatever
 * is left stays unallocated credit. Documents that get nothing are omitted.
 */
export function autoAllocate(
  amount: string,
  items: readonly OpenItem[],
  decimals = 2,
): AllocationDraft {
  let remaining = toUnits(amount) ?? zero;
  const out: AllocationDraft = {};
  if (remaining <= zero) return out;
  for (const item of fifoOrder(items)) {
    if (remaining <= zero) break;
    const open = toUnits(item.open) ?? zero;
    if (open <= zero) continue;
    const take = min(remaining, open);
    out[item.id] = fromUnits(take, decimals);
    remaining -= take;
  }
  return out;
}

export type ExcessPolicy = 'KEEP_AS_CREDIT' | 'APPLY_FIFO';

/**
 * Payment against one document (ACCOUNTING_RULES §4.2): that document first, up to its open
 * amount; the excess stays as credit (KEEP_AS_CREDIT) or goes oldest first to the others
 * (APPLY_FIFO).
 */
export function targetAllocate(
  amount: string,
  items: readonly OpenItem[],
  targetId: string,
  policy: ExcessPolicy,
  decimals = 2,
): AllocationDraft {
  const total = toUnits(amount) ?? zero;
  const target = items.find((i) => i.id === targetId);
  if (!target || total <= zero)
    return autoAllocate(policy === 'APPLY_FIFO' ? amount : '0', items, decimals);
  const take = min(total, toUnits(target.open) ?? zero);
  const out: AllocationDraft = take > zero ? { [target.id]: fromUnits(take, decimals) } : {};
  const excess = total - take;
  if (policy === 'APPLY_FIFO' && excess > zero) {
    Object.assign(
      out,
      autoAllocate(
        fromUnits(excess, SCALE),
        items.filter((i) => i.id !== targetId),
        decimals,
      ),
    );
  }
  return out;
}

export interface AllocationSummary {
  amount: bigint;
  allocated: bigint;
  /** amount − allocated; what stays as credit (negative = over-allocated). */
  remaining: bigint;
  /** Per document: why its amount is not acceptable. */
  errors: Record<string, string>;
  /** Too much allocated in total. */
  overAllocated: boolean;
  valid: boolean;
}

/** Checks a draft against the payment amount and each document's open amount. */
export function summarize(
  amount: string,
  draft: AllocationDraft,
  items: readonly OpenItem[],
): AllocationSummary {
  const total = toUnits(amount) ?? zero;
  const errors: Record<string, string> = {};
  let allocated = zero;
  const byId = new Map(items.map((i) => [i.id, i]));
  for (const [id, raw] of Object.entries(draft)) {
    if (raw.trim() === '') continue;
    const units = toUnits(raw);
    const item = byId.get(id);
    if (units === null || units < zero) {
      errors[id] = 'Enter a valid amount';
      continue;
    }
    if (item && units > (toUnits(item.open) ?? zero)) errors[id] = 'More than is open';
    allocated += units;
  }
  const remaining = total - allocated;
  const overAllocated = remaining < zero;
  return {
    amount: total,
    allocated,
    remaining,
    errors,
    overAllocated,
    valid: !overAllocated && Object.keys(errors).length === 0,
  };
}

/** The non-zero lines of a draft, in FIFO order, ready for the API. */
export function draftToLines<T extends OpenItem>(
  draft: AllocationDraft,
  items: readonly T[],
  decimals = 2,
): { item: T; amount: string }[] {
  return fifoOrder(items).flatMap((item) => {
    const units = toUnits(draft[item.id] ?? '');
    return units !== null && units > zero ? [{ item, amount: fromUnits(units, decimals) }] : [];
  });
}

/** Sum of decimal strings (ignores blanks and invalid entries). */
export function sum(values: readonly (string | null | undefined)[]): bigint {
  return values.reduce<bigint>((s, v) => s + (toUnits(v) ?? zero), zero);
}

/** Share of `part` in `whole` as a 0–100 percentage (for progress bars). */
export function percent(part: string, whole: string): number {
  const w = toUnits(whole) ?? zero;
  if (w <= zero) return 0;
  const p = toUnits(part) ?? zero;
  return Math.max(0, Math.min(100, Number((p * 10000n) / w) / 100));
}

/** Allocatable targets understood by the API (other open item types are informational only). */
export const ALLOCATABLE_TARGETS = [
  'SALES_INVOICE',
  'SUPPLIER_INVOICE',
  'OPENING_BALANCE',
] as const;
export type AllocatableTarget = (typeof ALLOCATABLE_TARGETS)[number];

export function isAllocatable(type: string): type is AllocatableTarget {
  return (ALLOCATABLE_TARGETS as readonly string[]).includes(type);
}
